import React, { useEffect, useState } from 'react';
import { API_BASE_URL } from '../config';

const DEFAULT = {
  connectionName: 'Traverser',
  clientId: 'phone_clint_1',
  brokerAddress: 'fleet-data.evrides.in',
  port: 2883,
  networkProtocol: 'TCP',
  topic: 'railway/command',
  username: 'gmr',
  password: '',
  keepAlive: 60,
  cleanSession: true,
  autoConnect: true,
  enableWillMessage: false,
  notifyOnDisconnect: false,
};

export default function SettingsPage() {
  const [form, setForm] = useState(DEFAULT);
  const [showPw, setShowPw] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [mqttStatus, setMqttStatus] = useState({ connected: false });

  useEffect(() => {
    fetch(`${API_BASE_URL}/api/settings`).then(r => r.json()).then(d => setForm(f => ({ ...f, ...d }))).catch(() => {});
    fetch(`${API_BASE_URL}/api/settings/mqtt-status`).then(r => r.json()).then(setMqttStatus).catch(() => {});
  }, []);

  function set(key, val) { setForm(f => ({ ...f, [key]: val })); }

  async function save() {
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/settings`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form) });
      const data = await res.json();
      if (data.success) { setSaved(true); setTimeout(() => setSaved(false), 3000); }
    } catch {}
    setSaving(false);
  }

  async function resetAll() {
    if (!window.confirm('Reset all workshop states to Available?')) return;
    setResetting(true);
    try {
      await fetch(`${API_BASE_URL}/api/workshops/reset-all`, { method: 'POST' });
      alert('All workshops reset to Available.');
    } catch { alert('Reset failed — check server.'); }
    setResetting(false);
  }

  return (
    <div>
      <div className="page-title">Settings</div>
      <div className="page-sub">MQTT connection &amp; system</div>

      {/* MQTT status pill */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14, background: '#fff', borderRadius: 10, padding: '10px 14px', border: '1px solid #dde3ed' }}>
        <div style={{ width: 10, height: 10, borderRadius: '50%', background: mqttStatus.connected ? '#43a047' : '#e53935' }} />
        <span style={{ fontSize: 12, fontWeight: 500, color: mqttStatus.connected ? '#1b5e20' : '#b71c1c' }}>
          {mqttStatus.connected ? 'MQTT broker connected' : 'MQTT broker disconnected'}
        </span>
      </div>

      {/* Connection */}
      <div className="settings-section">
        <div className="settings-section-header">Connection</div>
        <div className="settings-field">
          <div className="settings-label">Connection name</div>
          <input className="settings-input" value={form.connectionName} onChange={e => set('connectionName', e.target.value)} />
        </div>
        <div className="settings-field">
          <div className="settings-label">Client ID</div>
          <input className="settings-input" value={form.clientId} onChange={e => set('clientId', e.target.value)} />
        </div>
        <div className="settings-field">
          <div className="settings-label">Broker address <span className="required">*</span></div>
          <input className="settings-input" value={form.brokerAddress} onChange={e => set('brokerAddress', e.target.value)} />
        </div>
        <div className="settings-row">
          <div>
            <div className="settings-label">Port <span className="required">*</span></div>
            <input className="settings-input" type="number" value={form.port} onChange={e => set('port', parseInt(e.target.value))} />
          </div>
          <div>
            <div className="settings-label">Protocol</div>
            <select className="settings-input" value={form.networkProtocol} onChange={e => set('networkProtocol', e.target.value)}>
              <option>TCP</option>
              <option>WebSocket</option>
              <option>SSL/TLS</option>
            </select>
          </div>
        </div>
      </div>

      {/* Topic */}
      <div className="settings-section">
        <div className="settings-section-header">MQTT Topic</div>
        <div className="settings-field">
          <div className="settings-label">Topic <span className="required">*</span></div>
          <input className="settings-input" value={form.topic} onChange={e => set('topic', e.target.value)} />
        </div>
        <div style={{ padding: '8px 14px 10px', background: '#fffbea', borderTop: '0.5px solid #fde68a' }}>
          <p style={{ fontSize: 11, color: '#92400e' }}>
            Payload: <code style={{ background: '#fef3c7', padding: '1px 4px', borderRadius: 3 }}>f1</code> (ON) &nbsp;/&nbsp;
            <code style={{ background: '#fef3c7', padding: '1px 4px', borderRadius: 3 }}>f1_off</code> (OFF)
          </p>
        </div>
      </div>

      {/* Credentials */}
      <div className="settings-section">
        <div className="settings-section-header">Credentials</div>
        <div className="settings-row">
          <div>
            <div className="settings-label">Username</div>
            <input className="settings-input" value={form.username} onChange={e => set('username', e.target.value)} />
          </div>
          <div>
            <div className="settings-label">Password</div>
            <div style={{ position: 'relative' }}>
              <input className="settings-input" type={showPw ? 'text' : 'password'} value={form.password} onChange={e => set('password', e.target.value)} style={{ paddingRight: 36 }} />
              <button onClick={() => setShowPw(p => !p)} style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: '#5a6a80', cursor: 'pointer', padding: 0 }}>
                {showPw
                  ? <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                  : <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Advanced */}
      <div className="settings-section">
        <div className="settings-section-header">Advanced</div>
        <div className="settings-field">
          <div className="settings-label">Keep alive (seconds)</div>
          <input className="settings-input" type="number" value={form.keepAlive} onChange={e => set('keepAlive', parseInt(e.target.value))} />
        </div>
        <div className="checkbox-row">
          <input type="checkbox" id="ac" checked={form.autoConnect} onChange={e => set('autoConnect', e.target.checked)} />
          <label htmlFor="ac">Auto connect</label>
        </div>
        <div className="checkbox-row">
          <input type="checkbox" id="cs" checked={form.cleanSession} onChange={e => set('cleanSession', e.target.checked)} />
          <label htmlFor="cs">Clean session</label>
        </div>
        <div className="checkbox-row">
          <input type="checkbox" id="wm" checked={form.enableWillMessage} onChange={e => set('enableWillMessage', e.target.checked)} />
          <label htmlFor="wm">Enable will message</label>
        </div>
        <div className="checkbox-row">
          <input type="checkbox" id="nd" checked={form.notifyOnDisconnect} onChange={e => set('notifyOnDisconnect', e.target.checked)} />
          <label htmlFor="nd">Notify on disconnect</label>
        </div>
      </div>

      {/* Danger zone */}
      <div className="settings-section">
        <div className="settings-section-header" style={{ background: '#b71c1c' }}>Workshop State</div>
        <div className="settings-field">
          <p style={{ fontSize: 12, color: '#5a6a80', marginBottom: 10 }}>
            If workshop states are stuck from a previous session, use this to reset all to Available.
          </p>
          <button
            onClick={resetAll}
            disabled={resetting}
            style={{ background: '#fce4ec', color: '#b71c1c', border: '1.5px solid #e53935', borderRadius: 8, padding: '9px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer', width: '100%' }}
          >
            {resetting ? 'Resetting…' : 'Reset All Workshops to Available'}
          </button>
        </div>
      </div>

      {saved && <div className="toast toast-success" style={{ marginBottom: 12 }}>Settings saved — MQTT reconnecting…</div>}
      <button className="btn-primary" onClick={save} disabled={saving}>
        {saving ? 'Saving…' : 'Save & connect'}
      </button>
      <div style={{ height: 24 }} />
    </div>
  );
}
